import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ConfirmDialog, CloseIcon, TrashIcon } from "./v2";

// Destructive/irreversible confirmations on the agency surface — My Agency V2
// rebuild, 1:1 from ARTBOARD-SPEC board 4c ("Revoke invite confirmation" +
// binding Unlink pattern note) and board 2i ("Remove partner confirmation").
//   kind "revoke"  → revoke a pending partnership invite (SYM-06 icon), ALSO
//                    reused for a pending AGENT invite (DECISION-037): same
//                    sentence, same consequence, same reversibility
//   kind "unlink"  → unlink a linked agency — same pattern, per 4c:
//                    “Removing an already-linked agency uses this same
//                    pattern (‘Unlink’) — it disappears from both walls.”
//   kind "remove"  → remove a showcase/external partner (SYM-23 icon)
//   kind "declineInvite"  → decline an INCOMING partnership invite
//   kind "declineRequest" → decline an agent's request to join the agency
//
// DECISION-037 (Marin, 2026-07-29): "absichern. PartnerConfirmDialog vor (a)
// Agenten-Einladungs-Revoke, (b) eingehende Partner-Einladung ABLEHNEN, (c)
// Agent-Join-Request ABLEHNEN. Copy sinngemäss nach 4c-Vorlage." The two
// decline kinds have no board copy of their own — they follow 4c's shape
// (name the consequence, then name the way back) and reuse 4c's already
// translated "Decline" as the action label.
//
// The subject is always passed as `action.partner = { name }` — for the agent
// cases that reads as "the other party", not as a partner agency; callers
// resolve a display name themselves so the generic fallback below is never hit.
// PRESENTATION ONLY (Brief Rules 6/8): the caller owns the action state
// and the existing handlers (respondToPartnerInvite(id,'revoke') /
// removeShowcasePartner(id)); close stays blocked while loading exactly
// as before. Mobile renders the CMP-CONFIRM mobile variant (board 1n
// pattern) — DERIVED, no partners-mobile board exists in the handoff.

const COPY = {
  revoke: (name) => ({
    title: `Revoke invite to ${name}?`,
    body: "They’ll no longer be able to accept it. You can send a new invite anytime.",
    confirmLabel: "Revoke invite",
    icon: <CloseIcon size={18} strokeWidth={2} stroke="#DC2626" />,
    width: 420,
  }),
  unlink: (name) => ({
    title: `Unlink ${name}?`,
    body: "It disappears from both walls.",
    confirmLabel: "Unlink",
    icon: <CloseIcon size={18} strokeWidth={2} stroke="#DC2626" />,
    width: 420,
  }),
  remove: (name) => ({
    title: `Remove ${name}?`,
    body: "It disappears from your agency’s public showcase. You can add it again anytime.",
    confirmLabel: "Remove partner",
    icon: <TrashIcon size={18} />,
    width: 400,
  }),
  declineInvite: (name) => ({
    title: `Decline invite from ${name}?`,
    body: "Your agencies stay unlinked. They can send a new invite anytime.",
    confirmLabel: "Decline",
    icon: <CloseIcon size={18} strokeWidth={2} stroke="#DC2626" />,
    width: 420,
  }),
  declineRequest: (name) => ({
    title: `Decline ${name}’s request to join?`,
    body: "They won’t join your agency. You can invite them anytime.",
    confirmLabel: "Decline",
    icon: <CloseIcon size={18} strokeWidth={2} stroke="#DC2626" />,
    width: 420,
  }),
};

const PartnerConfirmDialog = ({ action, loading = false, onCancel, onConfirm }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  if (!action) return null;
  const name = action.partner?.name || "this partner";
  const cfg = (COPY[action.kind] || COPY.revoke)(name);
  return (
    <ConfirmDialog
      open
      tone="danger"
      mobile={isMobile}
      width={cfg.width}
      icon={cfg.icon}
      title={cfg.title}
      body={cfg.body}
      confirmLabel={cfg.confirmLabel}
      cancelLabel="Cancel"
      loading={loading}
      onClose={onCancel}
      onConfirm={onConfirm}
    />
  );
};

export default PartnerConfirmDialog;
